import { useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import axios from 'axios';

// homepage_refactor.md - "Have a question?" is a lighter-weight ask than "Request a demo"
// (RequestDemoModal): just name/email/message, same popup treatment, same backend endpoint
// (it lands in the same RentalDemoRequest queue the website owner/marketing already work).
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const empty = { name: '', email: '', message: '' };

export default function HaveAQuestionModal({ show, onHide }) {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState('');

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const close = () => {
    onHide();
    setTimeout(() => { setForm(empty); setErrors({}); setDone(false); setServerError(''); }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = 'Please tell us your name';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Enter a valid email';
    if (!form.message.trim()) next.message = 'Tell us how we can help';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSubmitting(true);
    setServerError('');
    try {
      await axios.post(`${API_BASE_URL}/api/demo-requests`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim()
      });
      setDone(true);
    } catch (err) {
      setServerError(err.response?.data?.msg || 'Could not send that message. Check the API is running, then try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title>Have a question?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {done ? (
          <div>
            <Alert variant="success" className="mb-3">
              Thanks — your message is in! Our support team will get back to you within one business day.
            </Alert>
            <Button variant="outline-primary" size="sm" onClick={() => { setDone(false); setForm(empty); }}>
              Send another message
            </Button>
          </div>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            {serverError && <Alert variant="danger">{serverError}</Alert>}
            <Form.Group className="mb-2" controlId="haq-name">
              <Form.Label className="small mb-0">Your name *</Form.Label>
              <Form.Control size="sm" value={form.name} onChange={set('name')} isInvalid={!!errors.name} />
              <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="haq-email">
              <Form.Label className="small mb-0">Email *</Form.Label>
              <Form.Control size="sm" type="email" value={form.email} onChange={set('email')} isInvalid={!!errors.email} />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="haq-message">
              <Form.Label className="small mb-0">How can we help? *</Form.Label>
              <Form.Control size="sm" as="textarea" rows={3} value={form.message} onChange={set('message')} isInvalid={!!errors.message} />
              <Form.Control.Feedback type="invalid">{errors.message}</Form.Control.Feedback>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button size="sm" variant="outline-secondary" onClick={close}>Cancel</Button>
              <Button size="sm" type="submit" disabled={submitting}>{submitting ? 'Sending…' : 'Send message'}</Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}
